import { useState, useMemo, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch } from "react-redux";
import {
  fetchCarouselRequest,
  deleteCarouselLocationRequest,
} from "../../../redux/slices/CarouselSlice";
import {
  Card,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  CircularProgress,
  useMediaQuery,
  TextField,
  InputAdornment,
  Skeleton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import EditCarousel from "./create-carousel/EditCarousel";
import axios from "axios";
import { toast } from "react-toastify";
import { canShowPermission } from "../Permissions/permissionObject";
import ConfirmationDialog from "../../Modals/ConfirmationModal";
import { carouselOutlinedCard } from "./carouselStyles";

const StatusToggle = ({ checked, loading, onChange, ariaLabel }) => (
  <FormControlLabel
    sx={{ ml: 0, mr: 0 }}
    control={
      <Switch
        size="small"
        color="success"
        checked={!!checked}
        disabled={loading}
        onChange={onChange}
        inputProps={{ "aria-label": ariaLabel }}
      />
    }
    label={
      loading ? (
        <CircularProgress size={14} />
      ) : (
        <Typography
          variant="caption"
          fontWeight={600}
          color={checked ? "success.main" : "text.secondary"}
        >
          {checked ? "Enabled" : "Disabled"}
        </Typography>
      )
    }
  />
);

const CarouselTable = ({ screenFilter = "", loading = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const dispatch = useDispatch();
  const [loadingNavigator, setLoadingNavigator] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [locationDeleteDialogOpen, setLocationDeleteDialogOpen] = useState(false);
  const { data: carouselState, locationActionLoading } = useSelector(
    (state) => state.carousel
  );
  const carousellist = useMemo(() => {
    let list = carouselState?.carousellist || [];
    if (screenFilter) {
      list = list.filter((c) => c?.location?.startsWith(screenFilter));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.label || "").toLowerCase().includes(q) ||
          (c.location || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [carouselState, screenFilter, search]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "carouselsettings", "edit");
  const canDelete = canShowPermission(permissions, "carouselsettings", "delete");

  const handleEditClick = (row) => {
    setFormValues({
      carouselPosition: row.location,
      locationLabel: row.label || row.location,
      carouselHeight: row.height,
      carouselDuration: row.duration,
      enabled: row.enabled,
      shownavigator: row.shownavigator,
      isSystem: row.isSystem,
      id: row._id,
    });
    setEditOpen(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCloseModal = () => {
    setEditOpen(false);
  };

  const handleToggleNavigator = async (args, type) => {
    if (type === "showNavigator") {
      setLoadingNavigator(args._id);
    } else if (type === "enabled") {
      setLoadingStatus(args._id);
    }

    try {
      const payload = {
        edit: {
          id: args._id,
          location: args.location,
          height: args.height,
          duration: args.duration,
          enabled: type === "enabled" ? !args.enabled : args.enabled,
          shownavigator:
            type === "showNavigator" ? !args.shownavigator : args.shownavigator,
          edit: true,
        },
      };

      setTimeout(async () => {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/v1/carousel/savecorousel`,
            payload
          );

          dispatch(fetchCarouselRequest());

          if (type === "enabled") {
            toast.success(
              `Carousel ${!args.enabled ? "enabled" : "disabled"} successfully.`
            );
          } else if (type === "showNavigator") {
            toast.success(
              `Navigator ${
                !args.shownavigator ? "shown" : "hidden"
              } successfully.`
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          toast.error("Failed to update carousel.");
        } finally {
          if (type === "showNavigator") setLoadingNavigator(null);
          if (type === "enabled") setLoadingStatus(null);
        }
      }, 2000);
    } catch (error) {
      console.error("Error preparing update:", error);
      if (type === "showNavigator") setLoadingNavigator(null);
      if (type === "enabled") setLoadingStatus(null);
    }
  };

  const handleConfirmDeleteLocation = () => {
    if (!locationToDelete?._id) return;
    dispatch(deleteCarouselLocationRequest(locationToDelete._id));
    setLocationDeleteDialogOpen(false);
    setLocationToDelete(null);
  };

  let rowCss = {
    box: {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    typography: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      textAlign: "center",
    },
  };

  const columns = [
    {
      flex: 0.25,
      minWidth: 180,
      field: "location",
      headerName: "Location",
      align: "center",
      headerAlign: "center",
      headerClassName: "custom-header",
      cellClassName: "custom-cell",
      renderCell: ({ row }) => (
        <Box sx={rowCss.box}>
          <Typography noWrap variant="body2" sx={row.typography}>
            {row.label || row.location}
            {row.isSystem === false ? " (custom)" : ""}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.12,
      minWidth: 100,
      field: "height",
      headerName: "Height",
      align: "center",
      headerAlign: "center",
      headerClassName: "custom-header",
      cellClassName: "custom-cell",
      renderCell: ({ row }) => (
        <Box sx={rowCss.box}>
          <Typography noWrap variant="body2" sx={row.typography}>
            {row.height}
          </Typography>
        </Box>
      ),
    },
    ...(canEdit
      ? [
          {
            flex: 0.16,
            minWidth: 150,
            field: "shownavigator",
            headerName: "Navigator",
            align: "center",
            headerAlign: "center",
            headerClassName: "custom-header",
            cellClassName: "custom-cell",
            renderCell: ({ row }) => {
              const isLoading = loadingNavigator === row._id;
              return (
                <Button
                  variant="outlined"
                  color={row.shownavigator ? "success" : "error"}
                  size="small"
                  onClick={() => handleToggleNavigator(row, "showNavigator")}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : row.shownavigator ? (
                    "Shown"
                  ) : (
                    "Hidden"
                  )}
                </Button>
              );
            },
          },
        ]
      : []),
    ...(canEdit
      ? [
          {
            flex: 0.14,
            minWidth: 130,
            field: "enabled",
            headerName: "Status",
            align: "center",
            headerAlign: "center",
            headerClassName: "custom-header",
            cellClassName: "custom-cell",
            renderCell: ({ row }) => {
              const isLoading = loadingStatus === row._id;
              return (
                <StatusToggle
                  checked={row.enabled}
                  loading={isLoading}
                  onChange={() => handleToggleNavigator(row, "enabled")}
                  ariaLabel={row.enabled ? "Disable carousel" : "Enable carousel"}
                />
              );
            },
          },
        ]
      : []),
    {
      flex: 0.12,
      minWidth: 110,
      field: "duration",
      headerClassName: "custom-header",
      cellClassName: "custom-cell",
      headerName: "Duration",
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Box sx={rowCss.box}>
          <Typography noWrap variant="body2" sx={rowCss.typography}>
            {row.duration}
          </Typography>
        </Box>
      ),
    },
    ...(canEdit
      ? [
          {
            flex: 0.12,
            minWidth: 110,
            field: "actions",
            headerName: "Edit",
            align: "center",
            headerAlign: "center",
            headerClassName: "custom-header",
            cellClassName: "custom-cell",
            renderCell: ({ row }) => (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                size="small"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </Button>
            ),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            flex: 0.12,
            minWidth: 110,
            field: "delete",
            headerName: "Delete",
            align: "center",
            headerAlign: "center",
            headerClassName: "custom-header",
            cellClassName: "custom-cell",
            renderCell: ({ row }) =>
              row.isSystem === false ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  size="small"
                  disabled={locationActionLoading}
                  onClick={() => {
                    setLocationToDelete(row);
                    setLocationDeleteDialogOpen(true);
                  }}
                >
                  Delete
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              ),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search locations..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: { sm: 360 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={isMobile ? 140 : 52} />
          ))}
        </Stack>
      ) : isMobile ? (
        <Stack
          spacing={1.5}
          sx={{
            maxHeight: 480,
            overflowY: "auto",
            pr: 0.5,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {carousellist.map((row) => (
            <Card
              key={row._id}
              variant="outlined"
              sx={{ p: 2, ...carouselOutlinedCard }}
            >
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography fontWeight={700} sx={{ wordBreak: "break-word" }}>
                    {row.label || row.location}
                  </Typography>
                  {row.isSystem === false && (
                    <Chip label="Custom" size="small" color="secondary" />
                  )}
                </Stack>

                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Height: <b>{row.height}</b>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: <b>{row.duration}</b>
                  </Typography>
                </Stack>

                {canEdit && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      size="small"
                      color={row.shownavigator ? "success" : "error"}
                      onClick={() => handleToggleNavigator(row, "showNavigator")}
                      disabled={loadingNavigator === row._id}
                    >
                      {loadingNavigator === row._id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : row.shownavigator ? (
                        "Navigator: Shown"
                      ) : (
                        "Navigator: Hidden"
                      )}
                    </Button>
                    <StatusToggle
                      checked={row.enabled}
                      loading={loadingStatus === row._id}
                      onChange={() => handleToggleNavigator(row, "enabled")}
                      ariaLabel={
                        row.enabled ? "Disable carousel" : "Enable carousel"
                      }
                    />
                  </Stack>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {canEdit && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(row)}
                    >
                      Edit
                    </Button>
                  )}
                  {canDelete && row.isSystem === false && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      disabled={locationActionLoading}
                      onClick={() => {
                        setLocationToDelete(row);
                        setLocationDeleteDialogOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
          {carousellist.length === 0 && (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
                border: "1px dashed #e0d8f5",
                borderRadius: 2,
                bgcolor: "#faf8ff",
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {search
                  ? "No locations match your search."
                  : screenFilter
                  ? `No locations found for "${screenFilter}".`
                  : "No carousel locations found."}
              </Typography>
              {!search && screenFilter && (
                <Typography variant="caption" color="text.secondary">
                  Use &quot;Add Location&quot; to create a custom carousel.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      ) : (
        <Card variant="outlined" sx={carouselOutlinedCard}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "#fff",
              borderRadius: "3px",
              overflow: "hidden",

              "& .custom-header": {
                backgroundColor: "#a56affff !important",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "12px 16px",
              },

              "& .custom-cell": {
                position: "relative",
                fontSize: "14px",
                backgroundColor: "#fff",
                borderBottom: "1px solid #e0e0e0",

                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "8px",
                  bottom: "8px",
                  right: 0,
                  width: "1px",
                  backgroundColor: "#e0e0e0",
                },
              },

              "& .MuiDataGrid-row .MuiDataGrid-cell:last-child::after": {
                display: "none",
              },

              "& .MuiDataGrid-columnHeaders": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#f5f5f5",
              },

              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#fff",
              },
            }}
          >
            <DataGrid
              rows={carousellist}
              getRowId={(row) => row._id}
              columns={columns}
              disableSelectionOnClick
              hideFooter={carousellist.length <= 10}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 25, 50]}
              sx={{
                width: "100%",
                border: "none",
                minWidth: 0,
                height: 480,
                "& .MuiDataGrid-virtualScroller": {
                  overflowY: "auto !important",
                  overflowX: "auto !important",
                },
                "& .MuiDataGrid-main": {
                  overflow: "hidden",
                },
              }}
            />
          </Box>
        </Card>
      )}
      <EditCarousel
        open={editOpen}
        onClose={handleCloseModal}
        formValues={formValues}
        onChange={handleInputChange}
      />
      <ConfirmationDialog
        open={locationDeleteDialogOpen}
        onClose={() => {
          setLocationDeleteDialogOpen(false);
          setLocationToDelete(null);
        }}
        onConfirm={handleConfirmDeleteLocation}
        title="Delete location?"
        content={`Delete custom location "${
          locationToDelete?.label || locationToDelete?.location || ""
        }" and its slides? This cannot be undone.`}
      />
    </Box>
  );
};

export default CarouselTable;
